// TODO: review this file
import { StoryblokBridgeV2 } from '@storyblok/react';
import StoryblokClient, { ISbStoriesParams } from 'storyblok-js-client';
import { BrowserDetectionService } from '../browser-detection';
import { StoryblokLinkNode } from '../navigation/types';
import {
  Datasource,
  StoryblokDatasourceResult,
  // APIConnectorPluginSettings,
  WindowWithStoryblokBridge,
  getUrlParams,
} from './types';

export class StoryblokService {
  private static client: StoryblokClient | null = null;

  private static initializeClient() {
    if (!this.client) {
      this.client = new StoryblokClient({
        accessToken: process.env.GATSBY_STORYBLOK_SPACE_API_KEY as string,
      });
    }
  }

  private static getClient(): StoryblokClient {
    if (!this.client) {
      this.initializeClient();
    }
    return this.client as StoryblokClient;
  }

  static getObject(): StoryblokBridgeV2 | undefined {
    if (BrowserDetectionService.isBrowserContext()) {
      const windowWithStoryblok = window as WindowWithStoryblokBridge;
      if (windowWithStoryblok.StoryblokBridge) {
        return new windowWithStoryblok.StoryblokBridge();
      }
    }
    return undefined;
  }

  static async getAllStories(params: ISbStoriesParams): Promise<any> {
    const client = this.getClient();
    const { data } = await client.get('cdn/stories', params);
    return data.stories;
  }

  static async redirect(callback?: (...args: any[]) => void): Promise<void> {
    const { redirect, path } = getUrlParams();
    const isValidRedirect = typeof redirect === 'string';

    if (!redirect) return;

    if (!this.getObject()) {
      const queryParameters = path ? `?path=${path}` : '';
      window.location.href = isValidRedirect ? `${redirect}${queryParameters}` : '/';
      return;
    }

    const client = new StoryblokClient({
      accessToken: process.env.GATSBY_STORYBLOK_SPACE_API_KEY as string,
    });

    try {
      const story = await client.getStory(redirect as string, {});
      if (callback) callback(story);
    } finally {
      // Remove the unnecessary return statement here
    }
  }

  // static isPreviewContent(): boolean {
  //   return this.getConfig().options.headers.Version === 'draft';
  // }

  static async getDatasourcesEntries(slugs: string[], params: Record<string, string | number> = {}): Promise<any[]> {
    const client = this.getClient();
    const fetchEntries = async (slug: string, page: number = 1) => {
      const response = await client.get('cdn/datasource_entries', {
        datasource: slug,
        per_page: 100,
        page,
        ...params,
      });
      return {
        ...response,
        slug,
        params,
        page,
      };
    };

    const initialResponses: StoryblokDatasourceResult[] = await Promise.all(
      slugs.map(async (slug) => fetchEntries(slug)),
    );

    const additionalRequests = initialResponses.flatMap((ds) => {
      const totalPages = Math.ceil(ds.total / ds.perPage);
      return Array.from({ length: totalPages - 1 }, (_, i) => ({
        slug: ds.slug,
        page: i + 2,
        params,
      }));
    });

    const additionalResponses: StoryblokDatasourceResult[] = await Promise.all(
      additionalRequests.map(async (req) => fetchEntries(req.slug, req.page)),
    );

    return this.combineDatasourceResponses(initialResponses, additionalResponses);
  }

  private static combineDatasourceResponses(
    allDatasources: StoryblokDatasourceResult[],
    newDatasources: StoryblokDatasourceResult[],
  ): StoryblokDatasourceResult[] {
    newDatasources.forEach((ds) => {
      const target = allDatasources.find(
        (d) => d.slug === ds.slug && JSON.stringify(d.params) === JSON.stringify(ds.params),
      );
      if (target) {
        target.data.datasource_entries.push(...ds.data.datasource_entries);
        target.perPage += ds.perPage;
      }
    });
    return allDatasources;
  }

  static async getDatasourceDimensionNames(client: StoryblokClient, slug: string): Promise<Datasource[]> {
    const { data } = await client.get('cdn/datasources', {
      per_page: 100,
    });
    return data.datasources.find((d: any) => d.slug === slug)?.dimensions || [];
  }

  static isInEditor(): boolean {
    return !!this.getObject();
  }

  static async getLinks(path: string): Promise<StoryblokLinkNode[]> {
    const client = this.getClient();
    const { data } = await client.get('cdn/links', {
      starts_with: path,
    });
    return Object.values(data.links);
  }
}
